/**
 * Конфигурация системы подписок Star AI
 *
 * ВАЖНО: Цены подписок теперь могут загружаться динамически с бэкенда.
 * SUBSCRIPTION_TIERS использует Proxy для автоматического мерджа
 * динамических цен из PricingStore.
 * Fallback на статические значения если бэкенд недоступен.
 *
 * @deprecated allowedModels/allowedProviders - FALLBACK ONLY, не редактировать!
 * Актуальные данные о доступе к моделям приходят с бэкенда (tierAccess в chat-registry.js).
 */

import { getSubscriptionsSync } from '../stores/usePricingStore';

export type SubscriptionTier = 'free' | 'start' | 'basic' | 'pro' | 'max';

export interface SubscriptionLimits {
  // Лимиты чата
  dailyMessages: number;
  monthlyMessages: number;
  messageLength: number;
  contextHistory: number;
  
  // Лимиты веб-поиска и файлов
  webSearchPerDay: number; // Количество запросов с веб-поиском в день
  fileRequestsPerDay: number; // Количество запросов с файлами в день
  maxFilesPerMessage: number; // Максимум файлов в одном сообщении
  
  // Ограничения типов файлов по провайдеру
  // Для каждого провайдера можно указать какие типы файлов разрешены
  // Если не указано, используются все типы, поддерживаемые моделью
  fileTypeRestrictions?: {
    [provider: string]: {
      images?: boolean;
      pdf?: boolean;
      documents?: boolean;
      code?: boolean;
      spreadsheets?: boolean;
      text?: boolean;
      video?: boolean;
      audio?: boolean;
    };
  };
  
  // Лимиты моделей
  allowedProviders: string[];
  allowedModels: string[];
  
  // Функции
  webSearch: boolean;
  reasoning: boolean;
  reasoningLevels?: string[];
  fileAttachments: boolean;
  imageGeneration: boolean;
  videoGeneration: boolean;
  audioTranscription: boolean;
  
  // Лимиты на изображения
  dailyImages: number;
  monthlyImages: number;
  imageQuality: string | null;
  allowedImageModels?: string[];
  imageSizes?: string[];
  
  // Лимиты на видео
  dailyVideos: number;
  monthlyVideos: number;
  videoQuality: string | null;
  allowedVideoModels?: string[];
  videoDuration: number;
  
  // Лимиты на ассистентов
  maxAssistants: number;
}

export interface SubscriptionTierConfig {
  id: string;
  name: string;
  displayName: string;
  color: string;
  colorRGB: string;
  price: number;
  priceMonthly: number;
  priceYearly: number;
  tokensMonthly: number;
  tokensYearly: number;
  features: string[];
  limits: SubscriptionLimits;
}

/**
 * FALLBACK конфигурация подписок (используется если бэкенд недоступен)
 */
const FALLBACK_SUBSCRIPTION_TIERS: Record<SubscriptionTier, SubscriptionTierConfig> = {
  // Free tier - теперь как basic (100 стартовых токенов, все функции)
  free: {
    id: 'free',
    name: 'Free',
    displayName: 'Free',
    color: '#808080',
    colorRGB: '128, 128, 128',
    price: 0,
    priceMonthly: 0,
    priceYearly: 0,
    tokensMonthly: 100, // 100 стартовых токенов при регистрации
    tokensYearly: 100,
    features: [
      'account:subscription.free.feature1',
      'account:subscription.free.feature2',
      'account:subscription.free.feature3',
      'account:subscription.free.feature4'
    ],
    limits: {
      dailyMessages: -1, // Безлимит (ограничено токенами)
      monthlyMessages: -1,
      messageLength: 4000, // Как у basic
      contextHistory: 20,
      
      webSearchPerDay: -1, // Безлимит (ограничено токенами)
      fileRequestsPerDay: -1,
      maxFilesPerMessage: 3,

      // Убраны fileTypeRestrictions - все типы файлов доступны

      allowedProviders: ['chatgpt', 'gemini', 'claude', 'grok', 'deepseek'],
      allowedModels: [
        // Free модели
        'gpt-5.4-mini-2026-03-17',
        'gpt-5-2025-08-07',
        'gpt-5.4-2026-03-05',
        'gpt-5.5-2026-04-23',
        'gpt-4.1-2025-04-14',
        'gemini-3.1-flash-lite-preview',
        'gemini-3-flash-preview',
        'gemini-2.5-pro',
        'gemini-3-pro-preview',
        'gemini-3.1-pro-preview',
        'deepseek-v4-flash',
        'deepseek-v4-pro',
        'deepseek-chat',
        'deepseek-reasoner',
        'claude-haiku-4-5',
        'claude-sonnet-4-6',
        'grok-4.3'
      ],

      webSearch: true,
      reasoning: true, // Теперь доступен
      reasoningLevels: ['low', 'medium'], // Как у basic
      fileAttachments: true,
      imageGeneration: true,
      videoGeneration: true, // Теперь доступно
      audioTranscription: true, // Теперь доступно
      
      dailyImages: -1, // Безлимит (ограничено токенами)
      monthlyImages: -1,
      imageQuality: 'hd', // Как у basic
      allowedImageModels: [
        'nano-banana-pro',
        'nano-banana-2',
        'gpt-image-2',
        'gpt-image-1.5',
        'nano-banana',
        'seedream-4',
        'imagen-4-fast',
        'imagen-4-ultra',
        'higgsfield-soul',
        'runway-gen4-image',
        'runway-gen4-image-turbo',
        'reve',
        'kling-v2-1'
      ],
      imageSizes: ['512x512', '1024x1024', '1024x1792', '1792x1024'],
      
      dailyVideos: -1, // Безлимит (ограничено токенами)
      monthlyVideos: -1,
      videoQuality: 'standard', // Как у basic
      allowedVideoModels: ['higgsfield-lite', 'runway-turbo', 'seedance-1-0-lite-250428', 'seedance-1-0-pro-250528', 'seedance-1-0-pro-fast-251015', 'seedance-1-5-pro-251215', 'kling-v2-6', 'kling-v2-1', 'kling-v2-5-turbo'],
      videoDuration: 10,
      maxAssistants: 0
    }
  },

  // Старт — разовая покупка 390₽ / 700 токенов / 30 дней / Basic-level access (без bonus pool).
  // Лимиты идентичны Basic (см. ниже) — копируются в FALLBACK_SUBSCRIPTION_TIERS.start.limits ниже.
  start: {
    id: 'start',
    name: 'Start',
    displayName: 'Start',
    color: '#fbbf24',          // = --color-start-500 = --color-yellow-400
    colorRGB: '251, 191, 36',
    price: 390,
    priceMonthly: 390,
    priceYearly: 390,
    tokensMonthly: 700,
    tokensYearly: 700,
    features: [
      'account:subscription.start.feature1',
      'account:subscription.start.feature2',
      'account:subscription.start.feature3',
      'account:subscription.start.feature4'
    ],
    limits: {
      dailyMessages: -1,
      monthlyMessages: -1,
      messageLength: 4000,
      contextHistory: 20,
      webSearchPerDay: 6,
      fileRequestsPerDay: 6,
      maxFilesPerMessage: 3,
      allowedProviders: ['chatgpt', 'gemini', 'claude', 'grok', 'deepseek'],
      allowedModels: [
        'gpt-5-2025-08-07', 'gpt-5.4-2026-03-05', 'gpt-5.5-2026-04-23',
        'gpt-5.3-codex', 'gpt-5.4-mini-2026-03-17', 'gpt-4.1-2025-04-14',
        'gemini-3-pro-preview', 'gemini-3.1-pro-preview', 'gemini-2.5-pro',
        'gemini-3-flash-preview', 'gemini-3.1-flash-lite-preview',
        'deepseek-v4-flash', 'deepseek-v4-pro', 'deepseek-chat', 'deepseek-reasoner',
        'claude-haiku-4-5', 'claude-sonnet-4-6', 'grok-4.3'
      ],
      webSearch: true,
      reasoning: true,
      reasoningLevels: ['low', 'medium'],
      fileAttachments: true,
      imageGeneration: true,
      videoGeneration: true,
      audioTranscription: true,
      dailyImages: -1,
      monthlyImages: -1,
      imageQuality: 'hd',
      allowedImageModels: [
        'nano-banana-pro', 'nano-banana-2', 'gpt-image-2', 'gpt-image-1.5',
        'nano-banana', 'seedream-4', 'imagen-4-fast', 'imagen-4-ultra',
        'higgsfield-soul', 'runway-gen4-image', 'runway-gen4-image-turbo',
        'reve', 'kling-v2-1'
      ],
      imageSizes: ['512x512', '1024x1024', '1024x1792', '1792x1024'],
      dailyVideos: -1,
      monthlyVideos: -1,
      videoQuality: 'standard',
      // Старт = Basic-level access. Список совпадает с pricing/video/registry.js basic.allowedProviders.
      // Pro+ only (per registry.js tierAccess: ['pro', 'max']):
      // veo-3, veo-3.1-elements, seedance-2, kling-v3-std, kling-o3, kling-v2-1-master.
      allowedVideoModels: [
        'sora-2', 'sora-2-pro',
        'veo-3-fast', 'veo-3.1-lite', 'veo-3.1-fast', 'veo-3.1',
        'higgsfield-lite', 'higgsfield-standard', 'higgsfield-turbo',
        'runway-gen4-5', 'runway-turbo', 'runway-aleph', 'runway-act-two',
        'seedance-2-fast', 'grok-imagine-video',
        'seedance-1-5-pro-251215', 'seedance-1-0-pro-fast-251015',
        'seedance-1-0-pro-250528', 'seedance-1-0-lite-250428',
        'kling-v2-6', 'kling-o1',
        'kling-v2-5-turbo', 'kling-v2-1'
      ],
      videoDuration: 10,
      maxAssistants: 3
    }
  },

  basic: {
    id: 'basic',
    name: 'Basic',
    displayName: 'Basic',
    color: '#FFFFFF',
    colorRGB: '255, 255, 255',
    price: 790,
    priceMonthly: 790,
    priceYearly: 9480,
    tokensMonthly: 1450,
    tokensYearly: 17400,
    features: [
      'account:subscription.basic.feature1',
      'account:subscription.basic.feature2',
      'account:subscription.basic.feature3',
      'account:subscription.basic.feature4'
    ],
    limits: {
      dailyMessages: -1,
      monthlyMessages: -1,
      messageLength: 4000,
      contextHistory: 20,
      
      webSearchPerDay: 6,
      fileRequestsPerDay: 6,
      maxFilesPerMessage: 3,
      
      allowedProviders: ['chatgpt', 'gemini', 'claude', 'grok', 'deepseek'],
      allowedModels: [
        'gpt-5-2025-08-07',
        'gpt-5.4-2026-03-05',
        'gpt-5.5-2026-04-23',
        'gpt-5.3-codex',
        'gpt-5.4-mini-2026-03-17',
        'gpt-4.1-2025-04-14',
        'gemini-3-pro-preview',
        'gemini-3.1-pro-preview',
        'gemini-2.5-pro',
        'gemini-3-flash-preview',
        'gemini-3.1-flash-lite-preview',
        'deepseek-v4-flash',
        'deepseek-v4-pro',
        'deepseek-chat',
        'deepseek-reasoner',
        'claude-haiku-4-5',
        'claude-sonnet-4-6',
        'grok-4.3'
      ],

      webSearch: true,
      reasoning: true,
      reasoningLevels: ['low', 'medium'],
      fileAttachments: true,
      imageGeneration: true,
      videoGeneration: true,
      audioTranscription: true,
      
      dailyImages: -1,
      monthlyImages: -1,
      imageQuality: 'hd',
      allowedImageModels: [
        'nano-banana-pro',
        'nano-banana-2',
        'gpt-image-2',
        'gpt-image-1.5',
        'nano-banana',
        'seedream-4',
        'imagen-4-fast',
        'imagen-4-ultra',
        'higgsfield-soul',
        'runway-gen4-image',
        'runway-gen4-image-turbo',
        'reve',
        'kling-v2-1'
      ],
      imageSizes: ['512x512', '1024x1024', '1024x1792', '1792x1024'],
      
      dailyVideos: -1,
      monthlyVideos: -1,
      videoQuality: 'standard',
      // Должно совпадать с pricing/video/registry.js basic.allowedProviders.
      // Pro+ only (per registry.js tierAccess: ['pro', 'max']):
      // veo-3, veo-3.1-elements, seedance-2, kling-v3-std, kling-o3, kling-v2-1-master.
      allowedVideoModels: [
        'sora-2', 'sora-2-pro',
        'veo-3-fast', 'veo-3.1-lite', 'veo-3.1-fast', 'veo-3.1',
        'higgsfield-lite', 'higgsfield-standard', 'higgsfield-turbo',
        'runway-gen4-5', 'runway-turbo', 'runway-aleph', 'runway-act-two',
        'seedance-2-fast', 'grok-imagine-video',
        'seedance-1-5-pro-251215', 'seedance-1-0-pro-fast-251015',
        'seedance-1-0-pro-250528', 'seedance-1-0-lite-250428',
        'kling-v2-6', 'kling-o1',
        'kling-v2-5-turbo', 'kling-v2-1'
      ],
      videoDuration: 10,
      maxAssistants: 3
    }
  },

  pro: {
    id: 'pro',
    name: 'Pro',
    displayName: 'Pro',
    color: '#1b95e0',
    colorRGB: '27, 149, 224',
    price: 2400,
    priceMonthly: 2400,
    priceYearly: 24480,
    tokensMonthly: 4500,
    tokensYearly: 54000,
    features: [
      'account:subscription.pro.feature1',
      'account:subscription.pro.feature2',
      'account:subscription.pro.feature3',
      'account:subscription.pro.feature4'
    ],
    limits: {
      dailyMessages: -1,
      monthlyMessages: -1,
      messageLength: 8000,
      contextHistory: 50,
      
      webSearchPerDay: 18,
      fileRequestsPerDay: 18,
      maxFilesPerMessage: 5,
      
      allowedProviders: ['chatgpt', 'gemini', 'claude', 'grok', 'deepseek'],
      allowedModels: [
        'gpt-5.5-pro-2026-04-23',
        'gpt-5.4-pro',
        'gpt-5.4-pro-2026-03-05',
        'gpt-5-2025-08-07',
        'gpt-5.4-2026-03-05',
        'gpt-5.5-2026-04-23',
        'gpt-5.3-codex',
        'gpt-5.4-mini-2026-03-17',
        'gpt-4.1-2025-04-14',
        'gemini-3-pro-preview',
        'gemini-3.1-pro-preview',
        'gemini-2.5-pro',
        'gemini-3-flash-preview',
        'gemini-3.1-flash-lite-preview',
        'deepseek-v4-flash',
        'deepseek-v4-pro',
        'deepseek-chat',
        'deepseek-reasoner',
        'claude-haiku-4-5',
        'claude-sonnet-4-6',
        'claude-opus-4-6',
        'claude-opus-4-7',
        'grok-4.3'
      ],

      webSearch: true,
      reasoning: true,
      reasoningLevels: ['low', 'medium', 'high', 'xhigh'],
      fileAttachments: true,
      imageGeneration: true,
      videoGeneration: true,
      audioTranscription: true,

      dailyImages: -1,
      monthlyImages: -1,
      imageQuality: 'hd',
      allowedImageModels: [
        'nano-banana-pro',
        'nano-banana-2',
        'gpt-image-2',
        'gpt-image-1.5',
        'nano-banana',
        'seedream-4',
        'imagen-4-fast',
        'imagen-4-ultra',
        'higgsfield-soul',
        'runway-gen4-image',
        'runway-gen4-image-turbo',
        'reve',
        'kling-v2-1'
      ],
      imageSizes: ['512x512', '1024x1024', '1024x1792', '1792x1024'],

      dailyVideos: -1,
      monthlyVideos: -1,
      videoQuality: 'hd',
      allowedVideoModels: [
        'sora-2',
        'sora-2-pro',
        'veo-3-fast',
        'veo-3',
        'veo-3.1-fast',
        'veo-3.1-lite',
        'veo-3.1',
        'veo-3.1-elements',
        'higgsfield-lite',
        'higgsfield-standard',
        'higgsfield-turbo',
        'runway-gen4-5',
        'runway-turbo',
        'runway-aleph',
        'runway-act-two',
        'seedance-2-fast',
        'seedance-2',
        'seedance-1-0-lite-250428',
        'seedance-1-0-pro-250528',
        'seedance-1-0-pro-fast-251015',
        'seedance-1-5-pro-251215',
        'kling-v2-6',
        'kling-o1',
        'kling-o3',
        'kling-v2-1-master',
        'kling-v2-1',
        'kling-v2-5-turbo',
        'kling-v3-std',
        'kling-v3-pro',
        'grok-imagine-video'
      ],
      videoDuration: 20,
      maxAssistants: 5
    }
  },

  max: {
    id: 'max',
    name: 'MAX',
    displayName: 'MAX',
    color: '#7109AA',
    colorRGB: '113, 9, 170',
    price: 3990,
    priceMonthly: 3990,
    priceYearly: 43092,
    tokensMonthly: 8000,
    tokensYearly: 96000,
    features: [
      'account:subscription.max.feature1',
      'account:subscription.max.feature2',
      'account:subscription.max.feature3',
      'account:subscription.max.feature4'
    ],
    limits: {
      dailyMessages: -1,
      monthlyMessages: -1,
      messageLength: 32000,
      contextHistory: 100,
      
      webSearchPerDay: 18,
      fileRequestsPerDay: 18,
      maxFilesPerMessage: 5,
      
      allowedProviders: ['chatgpt', 'gemini', 'claude', 'grok', 'deepseek'],
      allowedModels: [
        'gpt-5.5-pro-2026-04-23',
        'gpt-5.4-pro',
        'gpt-5.4-pro-2026-03-05',
        'gpt-5-2025-08-07',
        'gpt-5.4-2026-03-05',
        'gpt-5.5-2026-04-23',
        'gpt-5.3-codex',
        'gpt-5.4-mini-2026-03-17',
        'gpt-4.1-2025-04-14',
        'gemini-3-pro-preview',
        'gemini-3.1-pro-preview',
        'gemini-2.5-pro',
        'gemini-3-flash-preview',
        'gemini-3.1-flash-lite-preview',
        'deepseek-v4-flash',
        'deepseek-v4-pro',
        'deepseek-chat',
        'deepseek-reasoner',
        'claude-haiku-4-5',
        'claude-sonnet-4-6',
        'claude-opus-4-6',
        'claude-opus-4-7',
        'grok-4.3'
      ],
      
      webSearch: true,
      reasoning: true,
      reasoningLevels: ['low', 'medium', 'high', 'xhigh'],
      fileAttachments: true,
      imageGeneration: true,
      videoGeneration: true,
      audioTranscription: true,
      
      dailyImages: -1,
      monthlyImages: -1,
      imageQuality: 'hd',
      allowedImageModels: [
        'nano-banana-pro',
        'nano-banana-2',
        'gpt-image-2',
        'gpt-image-1.5',
        'nano-banana',
        'seedream-4',
        'imagen-4-fast',
        'imagen-4-ultra',
        'higgsfield-soul',
        'runway-gen4-image',
        'runway-gen4-image-turbo',
        'reve',
        'kling-v2-1'
      ],
      imageSizes: ['512x512', '1024x1024', '1024x1792', '1792x1024', '2048x2048'],
      
      dailyVideos: -1,
      monthlyVideos: -1,
      videoQuality: 'hd',
      allowedVideoModels: [
        'sora-2',
        'sora-2-pro',
        'veo-3-fast',
        'veo-3',
        'veo-3.1-fast',
        'veo-3.1-lite',
        'veo-3.1',
        'veo-3.1-elements',
        'higgsfield-lite',
        'higgsfield-standard',
        'higgsfield-turbo',
        'runway-gen4-5',
        'runway-turbo',
        'runway-aleph',
        'runway-act-two',
        'seedance-2-fast',
        'seedance-2',
        'seedance-1-0-lite-250428',
        'seedance-1-0-pro-250528',
        'seedance-1-0-pro-fast-251015',
        'seedance-1-5-pro-251215',
        'kling-v2-6',
        'kling-o1',
        'kling-o3',
        'kling-v2-1-master',
        'kling-v2-1',
        'kling-v2-5-turbo',
        'kling-v3-std',
        'kling-v3-pro',
        'grok-imagine-video'
      ],
      videoDuration: 20,
      maxAssistants: 10
    }
  }
};

/**
 * Рассчитанные подписки с динамическими ценами из бэкенда
 *
 * Proxy паттерн: при обращении к SUBSCRIPTION_TIERS['basic'] и т.д.
 * сначала проверяются динамические данные из PricingStore.
 * Если доступны — мерджатся поверх fallback (обновляя только цены/токены).
 * Если нет — возвращается fallback.
 */
export const SUBSCRIPTION_TIERS: Record<SubscriptionTier, SubscriptionTierConfig> = new Proxy(
  FALLBACK_SUBSCRIPTION_TIERS,
  {
    get(target, prop: string) {
      if (prop === '__esModule' || typeof prop === 'symbol') return (target as any)[prop];

      const fallback = target[prop as SubscriptionTier];
      if (!fallback) return fallback;

      // Пробуем получить динамические данные из store
      const dynamicSubs = getSubscriptionsSync();
      if (!dynamicSubs) return fallback;

      const dynamic = dynamicSubs[prop];
      if (!dynamic) return fallback;

      // Мерджим только pricing-поля поверх fallback (сохраняем limits, colors, features)
      return {
        ...fallback,
        price: dynamic.priceMonthly ?? fallback.price,
        priceMonthly: dynamic.priceMonthly ?? fallback.priceMonthly,
        priceYearly: dynamic.priceYearly ?? fallback.priceYearly,
        tokensMonthly: dynamic.tokensMonthly ?? fallback.tokensMonthly,
        tokensYearly: dynamic.tokensYearly ?? fallback.tokensYearly,
      };
    },
    ownKeys(target) {
      return Object.keys(target);
    },
    getOwnPropertyDescriptor(target, prop) {
      if (prop in target) {
        return { enumerable: true, configurable: true, value: (SUBSCRIPTION_TIERS as any)[prop as string] };
      }
      return undefined;
    }
  }
);

/**
 * Проверяет, доступна ли модель для уровня подписки
 *
 * ВАЖНО: Это FALLBACK функция! Основные данные приходят с бэкенда через API.
 * Используется только когда API данные ещё не загружены.
 * Единый источник истины: pricing/constants/model-mappings.js
 */
export function hasModelAccess(
  subscriptionTier: SubscriptionTier | string,
  provider: string,
  model: string
): boolean {
  const tier = SUBSCRIPTION_TIERS[subscriptionTier as SubscriptionTier] || SUBSCRIPTION_TIERS.free;
  
  // Проверяем провайдера
  if (!tier.limits.allowedProviders.includes(provider)) {
    return false;
  }
  
  // Проверяем модель
  return tier.limits.allowedModels.includes(model);
}

/**
 * Получает информацию о подписке пользователя
 */
export function getSubscriptionInfo(subscriptionTier: SubscriptionTier | string): SubscriptionTierConfig {
  return SUBSCRIPTION_TIERS[subscriptionTier as SubscriptionTier] || SUBSCRIPTION_TIERS.free;
}

/**
 * Проверяет, доступен ли провайдер для уровня подписки
 * 
 * ВАЖНО: Это FALLBACK функция! Основные данные приходят с бэкенда через API.
 * Используется только когда API данные ещё не загружены.
 * Единый источник истины: pricing/constants/model-mappings.js
 */
export function hasProviderAccess(
  subscriptionTier: SubscriptionTier | string,
  provider: string
): boolean {
  const tier = SUBSCRIPTION_TIERS[subscriptionTier as SubscriptionTier] || SUBSCRIPTION_TIERS.free;
  return tier.limits.allowedProviders.includes(provider);
}
